/**
 * Global keyboard shortcuts hook.
 *
 * This hook listens for keyboard shortcuts when the action menu is closed
 * and executes the corresponding action if available and enabled.
 */

import { useEffect, useCallback, useRef } from "react";
import { isEditableElement } from "@thenounproject/lingo-core";
import { ActionContext } from "./types";
import { actionRegistry, initializeRegistry } from "./ActionRegistry";
import { useActionDeps } from "./useActionDeps";

/**
 * Check if a modal is currently open.
 */
function isModalOpen(): boolean {
  return document.querySelector('[data-modal-overlay="true"]') !== null;
}

/**
 * Check if the action menu is currently open.
 */
function isActionMenuOpen(): boolean {
  return document.querySelector('[data-action-menu="true"]') !== null;
}

/**
 * Hook that listens for global keyboard shortcuts and executes matching actions.
 *
 * @param getContext Function that returns the current ActionContext, or null if no valid context
 * @param inspectables Current selection
 * @param options Configuration options
 */
export function useGlobalShortcuts(getContext: () => ActionContext | null): void {
  // Initialize registry
  const initRef = useRef(false);
  if (!initRef.current) {
    initializeRegistry();
    initRef.current = true;
  }

  // Get performers
  const actionDeps = useActionDeps();

  // Stable callback for handling keydown
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Skip if in input element
      if (isEditableElement(event.target)) return;

      // Skip if modal is open
      if (isModalOpen()) return;

      // Skip if action menu is open (it handles its own keyboard events)
      if (isActionMenuOpen()) return;

      // Get current context
      const context = getContext();
      if (!context) return;

      // Skip if no selection
      if (context.selection.all.length === 0) return;

      // Try to find matching action
      const action = actionRegistry.getActionByShortcut(event, context, actionDeps);

      if (action && action.enabled) {
        // Prevent default browser behavior
        event.preventDefault();
        event.stopPropagation();

        // Execute action
        void action.perform();
      }
    },
    [getContext, actionDeps]
  );

  // Attach/detach listener
  useEffect(() => {
    // Use capture phase to intercept before other handlers
    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [handleKeyDown]);
}
